import { ESdChangeReason, IDisplayItem, IScreenDesign } from "@kuika/kuika-cl-model";
import classNames from "classnames";
import React, { createRef, KeyboardEvent, ReactNode } from "react";
import isEqual from "react-fast-compare";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../../shared/pure-sd";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import KuikaBadge from "../../../ui-kit/kuika-badge/kuika-badge";
import KuikaButton from "../../../ui-kit/kuika-button/kuika-button";
import KuikaCollapsePanel from "../../../ui-kit/kuika-collapse-panel/kuika-collapse-panel";
import KuikaDropdownMenu from "../../../ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import KuikaEmptyState from "../../../ui-kit/kuika-empty-state/kuika-empty-state";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import KuikaSelectbox from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaSmallMediumText from "../../../ui-kit/typography/SmallMediumText/SmallMediumText";
import KuikaTextMedium from "../../../ui-kit/typography/TextMedium/TextMedium";
import PropertyRowIcon from "./property-row-icon";
import "./shortcuts-panel.scss";

interface ShortcutEntry {
  key: string;
  componentId?: string;
  isEditing?: boolean;
}

export interface IShortcutsPanelState {
  isShortcutsPanelActive: boolean;
  isCapturingKeyCombo: boolean;
  currentKeyCombo: string;
  activeShortcuts: ShortcutEntry[];
  isAddingShortcut: boolean;
  editingShortcutKey: string | null;
  feedbackMessage: string | null;
  isReservedShortcut: boolean;
}

export interface IShortcutsPanelProps {}

interface IExpandIconProps {
  isActive?: boolean;
}

class ShortcutsPanel extends PureSdComponent<IShortcutsPanelProps & IWithSDProps, IShortcutsPanelState> {
  shortcutInputRef = createRef<HTMLInputElement>();

  editShortcutInputRefs: { [key: string]: any } = {};

  constructor(props: IShortcutsPanelProps & IWithSDProps) {
    super(props);
    this.state = {
      isShortcutsPanelActive: false,
      isCapturingKeyCombo: false,
      currentKeyCombo: "",
      activeShortcuts: [],
      isAddingShortcut: false,
      editingShortcutKey: null,
      feedbackMessage: null,
      isReservedShortcut: false
    };
  }

  getMetaKeyDisplay = (): string => {
    const platform = navigator.platform.toLowerCase();
    if (platform.startsWith("mac")) {
      return "Cmd";
    }
    if (platform.startsWith("win")) {
      return "Win";
    }
    return "Meta";
  };

  getAltKeyDisplay = (): string => {
    const platform = navigator.platform.toLowerCase();
    if (platform.startsWith("mac")) {
      return "Opt";
    }
    return "Alt";
  };

  componentDidMount() {
    this.loadExistingShortcuts();
  }

  componentDidUpdate(prevProps: IShortcutsPanelProps & IWithSDProps) {
    if (
      prevProps.sd.getScreenDesign().shortcuts !== this.props.sd.getScreenDesign().shortcuts ||
      !isEqual(prevProps.sd.getScreenDesign().shortcuts, this.props.sd.getScreenDesign().shortcuts)
    ) {
      this.loadExistingShortcuts();
    }
  }

  loadExistingShortcuts() {
    const sd = this.props.sd.getScreenDesign();
    if (sd.shortcuts) {
      const shortcuts: ShortcutEntry[] = Object.entries(sd.shortcuts).map(([key, componentId]) => ({
        key,
        componentId: componentId || undefined
      }));
      this.setState({ activeShortcuts: shortcuts });
    }
  }

  /** The kit's own turning chevron, carrying the test id the E2E suite opens this section by. */
  renderExpandIcon = (panelProps: IExpandIconProps): ReactNode => (
    <KuikaSvg
      data-testid="screenShortcutPropertyChevron"
      name="chevron-right"
      type="arrows"
      width={16}
      height={16}
      className={classNames("kuika-collapse-panel__chevron", {
        "kuika-collapse-panel__chevron--open": panelProps.isActive
      })}
    />
  );

  getAllDi = () => {
    let allDi: IDisplayItem[] = [];
    const sd: IScreenDesign = this.props.sd.getScreenDesign();
    this.fillAllDisplayItemsRecursive(sd, allDi);
    allDi = this.filterDisplayItemsByButtons(allDi);
    allDi.sort((a, b) =>
      (a.friendlyName || "").localeCompare(b.friendlyName || "", undefined, {
        numeric: true,
        sensitivity: "base"
      })
    );
    return allDi;
  };

  fillAllDisplayItemsRecursive = (sd: IScreenDesign, result: IDisplayItem[]) => {
    if (sd.displayItems)
      for (let i = 0; i < sd.displayItems.length; i++) {
        result.push(sd.displayItems[i]);
        this.getAllDisplayItemsOfDi(sd.displayItems[i], result);
      }
  };

  getAllDisplayItemsOfDi = (di: IDisplayItem, result: IDisplayItem[]) => {
    if (di.children)
      for (let i = 0; i < di.children.length; i++) {
        result.push(di.children[i]);
        this.getAllDisplayItemsOfDi(di.children[i], result);
      }
  };

  filterDisplayItemsByButtons = (allDi: IDisplayItem[]): IDisplayItem[] => {
    return allDi.filter((di) => di.item.name?.toLowerCase() === "button");
  };

  isReservedSystemShortcut = (keyCombo: string): boolean => {
    const commonSystemShortcuts = [
      "Meta+KeyX",
      "Ctrl+KeyX",
      "Meta+KeyC",
      "Ctrl+KeyC",
      "Meta+KeyV",
      "Ctrl+KeyV",
      "Meta+KeyA",
      "Ctrl+KeyA",
      "Meta+KeyZ",
      "Ctrl+KeyZ",
      "Meta+KeyY",
      "Ctrl+KeyY",
      "Shift+Meta+KeyZ",
      "Shift+Ctrl+KeyZ",
      "Meta+KeyF",
      "Ctrl+KeyF",
      "Meta+KeyG",
      "Ctrl+KeyG",
      "Meta+KeyH",
      "Ctrl+KeyH",
      "Meta+KeyS",
      "Ctrl+KeyS",
      "Meta+KeyL",
      "Ctrl+KeyL",
      "Meta+KeyT",
      "Ctrl+KeyT",
      "Meta+KeyN",
      "Ctrl+KeyN",
      "Meta+KeyW",
      "Ctrl+KeyW",
      "Meta+Tab",
      "Ctrl+Tab",
      "Shift+Meta+Backquote",
      "Shift+Ctrl+Tab",
      "Meta+Digit1",
      "Ctrl+Digit1",
      "Meta+Digit2",
      "Ctrl+Digit2",
      "Meta+Digit3",
      "Ctrl+Digit3",
      "Meta+Digit4",
      "Ctrl+Digit4",
      "Meta+Digit5",
      "Ctrl+Digit5",
      "Meta+Digit6",
      "Ctrl+Digit6",
      "Meta+Digit7",
      "Ctrl+Digit7",
      "Meta+Digit8",
      "Ctrl+Digit8",
      "Meta+Digit9",
      "Ctrl+Digit9",
      "Meta+Digit0",
      "Ctrl+Digit0",
      "Meta+ArrowLeft",
      "Ctrl+ArrowLeft",
      "Alt+ArrowLeft",
      "Meta+ArrowRight",
      "Ctrl+ArrowRight",
      "Alt+ArrowRight",
      "Meta+Equal",
      "Ctrl+Equal",
      "Meta+Minus",
      "Ctrl+Minus",
      "Meta+Digit0",
      "Ctrl+Digit0",
      "Meta+Shift+Delete",
      "Ctrl+Shift+Delete",
      "Meta+KeyP",
      "Ctrl+KeyP",
      "Meta+KeyQ",
      "Alt+F4",
      "Meta+KeyM",
      "Win+KeyM",
      "Meta+KeyD",
      "Win+KeyD",
      "Meta+Comma",
      "Ctrl+Comma",
      "F1",
      "F2",
      "F3",
      "F4",
      "F5",
      "F6",
      "F7",
      "F8",
      "F9",
      "F10",
      "F11",
      "F12"
    ];

    return commonSystemShortcuts.includes(keyCombo);
  };

  isSystemShortcut = (keyCombo: string): boolean => {
    const systemShortcuts = [
      "Meta+KeyX",
      "Ctrl+KeyX",
      "Meta+KeyC",
      "Ctrl+KeyC",
      "Meta+KeyV",
      "Ctrl+KeyV",
      "Meta+KeyA",
      "Ctrl+KeyA",
      "Meta+KeyZ",
      "Ctrl+KeyZ",
      "Meta+KeyY",
      "Ctrl+KeyY",
      "Shift+Meta+KeyZ",
      "Shift+Ctrl+KeyZ",
      "Meta+KeyS",
      "Ctrl+KeyS",
      "Meta+KeyP",
      "Ctrl+KeyP",
      "Meta+KeyQ",
      "Alt+F4",
      "Meta+KeyM",
      "Win+KeyM",
      "Meta+KeyD",
      "Win+KeyD",
      "Meta+Comma",
      "Ctrl+Comma",
      "F1",
      "F2",
      "F3",
      "F4",
      "F5",
      "F6",
      "F7",
      "F8",
      "F9",
      "F10",
      "F11",
      "F12"
    ];

    return systemShortcuts.includes(keyCombo);
  };

  isBrowserShortcut = (keyCombo: string): boolean => {
    const browserShortcuts = [
      "Meta+KeyF",
      "Ctrl+KeyF",
      "Meta+KeyG",
      "Ctrl+KeyG",
      "Meta+KeyH",
      "Ctrl+KeyH",
      "Meta+KeyL",
      "Ctrl+KeyL",
      "Meta+KeyT",
      "Ctrl+KeyT",
      "Meta+KeyN",
      "Ctrl+KeyN",
      "Meta+KeyW",
      "Ctrl+KeyW",
      "Meta+Tab",
      "Ctrl+Tab",
      "Shift+Meta+Backquote",
      "Shift+Ctrl+Tab",
      "Meta+Digit1",
      "Ctrl+Digit1",
      "Meta+Digit2",
      "Ctrl+Digit2",
      "Meta+Digit3",
      "Ctrl+Digit3",
      "Meta+Digit4",
      "Ctrl+Digit4",
      "Meta+Digit5",
      "Ctrl+Digit5",
      "Meta+Digit6",
      "Ctrl+Digit6",
      "Meta+Digit7",
      "Ctrl+Digit7",
      "Meta+Digit8",
      "Ctrl+Digit8",
      "Meta+Digit9",
      "Ctrl+Digit9",
      "Meta+Digit0",
      "Ctrl+Digit0",
      "Meta+ArrowLeft",
      "Ctrl+ArrowLeft",
      "Alt+ArrowLeft",
      "Meta+ArrowRight",
      "Ctrl+ArrowRight",
      "Alt+ArrowRight",
      "Meta+Equal",
      "Ctrl+Equal",
      "Meta+Minus",
      "Ctrl+Minus",
      "Meta+Shift+Delete",
      "Ctrl+Shift+Delete"
    ];

    return browserShortcuts.includes(keyCombo);
  };

  getShortcutWarningType = (keyCombo: string): "system" | "browser" | "none" => {
    if (this.isSystemShortcut(keyCombo)) {
      return "system";
    }
    if (this.isBrowserShortcut(keyCombo)) {
      return "browser";
    }
    return "none";
  };

  getWarningMessage = (warningType: "system" | "browser" | "none"): string => {
    switch (warningType) {
      case "system":
        return "This is a system shortcut and may not work as expected.";
      case "browser":
        return "This is a browser shortcut and may not work as expected.";
      default:
        return "Note: Some shortcuts may not work as expected due to browser or OS functions.";
    }
  };

  handleShortcutKeyCapture = (keyCombo: string) => {
    if (!keyCombo) return;

    const existingIndex: number = this.state.activeShortcuts.findIndex((s) => s.key === keyCombo);
    if (existingIndex !== -1 && this.state.editingShortcutKey !== keyCombo) {
      KMainFunctions.displayWarningNotification(
        `The shortcut "${keyCombo.split("+").map(this.getKeyDisplay).join(" + ")}" is already in use.`
      );
      this.setState({
        isCapturingKeyCombo: false,
        currentKeyCombo: "",
        isAddingShortcut: false,
        editingShortcutKey: null,
        isReservedShortcut: false
      });
      return;
    }

    const warningType = this.getShortcutWarningType(keyCombo);
    const isReserved = warningType !== "none";

    if (this.state.editingShortcutKey) {
      const newShortcuts = this.state.activeShortcuts.map((shortcut) => {
        if (shortcut.key === this.state.editingShortcutKey) {
          return {
            ...shortcut,
            key: keyCombo
          };
        }
        return shortcut;
      });

      this.setState({
        activeShortcuts: newShortcuts,
        isCapturingKeyCombo: false,
        currentKeyCombo: "",
        editingShortcutKey: null,
        isReservedShortcut: isReserved
      });

      this.updateShortcutsInScreenDesign(newShortcuts);
      KMainFunctions.displaySuccessNotification("Shortcut successfully updated!");
      return;
    }

    const newShortcuts = [...this.state.activeShortcuts, { key: keyCombo, componentId: undefined }];
    this.setState({
      activeShortcuts: newShortcuts,
      isCapturingKeyCombo: false,
      currentKeyCombo: "",
      isAddingShortcut: false,
      isReservedShortcut: isReserved
    });

    this.updateShortcutsInScreenDesign(newShortcuts);
    KMainFunctions.displaySuccessNotification("New shortcut successfully added!");
  };

  handleComponentSelect = (shortcutKey: string, value: number) => {
    const newShortcuts = this.state.activeShortcuts.map((shortcut) => {
      if (shortcut.key === shortcutKey) {
        const allDi = this.getAllDi();
        return {
          ...shortcut,
          componentId: value !== undefined ? allDi[value].id.toString() : undefined
        };
      }
      return shortcut;
    });

    this.setState({ activeShortcuts: newShortcuts });
    this.updateShortcutsInScreenDesign(newShortcuts);

    if (value !== undefined) {
      const allDi = this.getAllDi();
      KMainFunctions.displaySuccessNotification(`Button "${allDi[value].friendlyName}" assigned to shortcut.`);
    } else {
      KMainFunctions.displayWarningNotification("Button unassigned from shortcut.");
    }
  };

  updateShortcutsInScreenDesign = (shortcuts: ShortcutEntry[]) => {
    const sd = this.props.sd.getScreenDesign();
    if (!sd.shortcuts) {
      sd.shortcuts = {};
    }

    Object.keys(sd.shortcuts).forEach((key) => {
      delete sd.shortcuts![key];
    });

    shortcuts.forEach((shortcut) => {
      sd.shortcuts![shortcut.key] = shortcut.componentId || "";
    });

    this.props.sd.setScreenDesign(sd, ESdChangeReason.UpdateShortcuts);
  };

  getSelectedComponentForShortcut = (shortcutKey: string) => {
    const shortcut: ShortcutEntry | undefined = this.state.activeShortcuts.find((s) => s.key === shortcutKey);

    if (!shortcut || !shortcut.componentId) {
      return undefined;
    }

    const allDi: IDisplayItem[] = this.getAllDi();
    const index: number = allDi.findIndex((di) => di.id.toString() === shortcut.componentId);
    return index >= 0 ? index : undefined;
  };

  startCapturingKeyCombo = () => {
    this.setState({ isCapturingKeyCombo: true, currentKeyCombo: "" }, () => {
      this.shortcutInputRef.current?.focus();
    });
  };

  startEditingShortcut = (shortcutKey: string) => {
    this.setState({
      editingShortcutKey: shortcutKey,
      isCapturingKeyCombo: true,
      currentKeyCombo: ""
    });
  };

  cancelEditingShortcut = () => {
    this.setState({
      editingShortcutKey: null,
      isCapturingKeyCombo: false,
      currentKeyCombo: ""
    });
  };

  handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!this.state.isCapturingKeyCombo) return;
    e.preventDefault();

    if (e.key === "Escape") {
      if (this.state.editingShortcutKey) {
        this.cancelEditingShortcut();
      } else if (this.state.isAddingShortcut) {
        this.cancelAddingShortcut();
      }
      return;
    }

    const pressedCode = e.code;
    const pressedVisibleKey = e.key;

    const activeModifiersForSubmission: string[] = [];
    const activeModifiersForDisplay: string[] = [];

    if (e.metaKey) {
      activeModifiersForSubmission.push("Meta");
      activeModifiersForDisplay.push(this.getMetaKeyDisplay());
    }
    if (e.ctrlKey) {
      activeModifiersForSubmission.push("Ctrl");
      activeModifiersForDisplay.push("Ctrl");
    }
    if (e.altKey) {
      activeModifiersForSubmission.push("Alt");
      activeModifiersForDisplay.push(this.getAltKeyDisplay());
    }
    if (e.shiftKey) {
      activeModifiersForSubmission.push("Shift");
      activeModifiersForDisplay.push("Shift");
    }

    const isCurrentKeyModifier = ["Control", "Alt", "Shift", "Meta", "Hyper", "Super", "OS"].includes(e.key);

    if (isCurrentKeyModifier) {
      this.setState({
        currentKeyCombo: activeModifiersForDisplay.join("+")
      });
    } else if (activeModifiersForSubmission.length > 0) {
      const visibleKey = this.getSingleKeyDisplay(pressedVisibleKey, pressedCode);

      this.setState({
        currentKeyCombo: [...activeModifiersForDisplay, visibleKey].join("+")
      });

      const keyComboForSubmission = [...activeModifiersForSubmission, pressedCode].join("+");

      this.handleShortcutKeyCapture(keyComboForSubmission);
    } else {
      this.setState({ currentKeyCombo: "" });
    }
  };

  handleKeyUp = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!this.state.isCapturingKeyCombo) return;

    const activeModifiersForDisplay: string[] = [];
    if (e.metaKey) activeModifiersForDisplay.push(this.getMetaKeyDisplay());
    if (e.ctrlKey) activeModifiersForDisplay.push("Ctrl");
    if (e.altKey) activeModifiersForDisplay.push(this.getAltKeyDisplay());
    if (e.shiftKey) activeModifiersForDisplay.push("Shift");

    this.setState({ currentKeyCombo: activeModifiersForDisplay.join("+") });
  };

  deleteShortcut = (shortcutKey: string) => {
    const newShortcuts = this.state.activeShortcuts.filter((shortcut) => shortcut.key !== shortcutKey);
    this.setState({ activeShortcuts: newShortcuts });
    this.updateShortcutsInScreenDesign(newShortcuts);
    KMainFunctions.displaySuccessNotification("Shortcut successfully deleted");
  };

  startAddingShortcut = () => {
    this.setState({ isAddingShortcut: true }, () => {
      this.startCapturingKeyCombo();
    });
  };

  cancelAddingShortcut = () => {
    this.setState({
      isAddingShortcut: false,
      isCapturingKeyCombo: false,
      currentKeyCombo: ""
    });
  };

  getShortcutPlaceholderText = (): string => {
    return this.state.isCapturingKeyCombo ? "Press shortcut keys or ESC to cancel..." : "Click to set shortcut";
  };

  renderLiveShortcutBadges = (keyComboString: string, placeholderText: string): JSX.Element => {
    if (!keyComboString) {
      return <span style={{ color: "var(--control-placeholder)", userSelect: "none" }}>{placeholderText}</span>;
    }

    const parts = keyComboString.split("+");
    return (
      <div className="shortcut-badge-container">
        {parts.map((part, index) => (
          <React.Fragment key={`${keyComboString}-${this.getKeyDisplay(part)}-${index}`}>
            <KuikaBadge content={this.getKeyDisplay(part)} color="blue" />
            {index < parts.length - 1 && <KuikaSvg name="plus" type="common" width={12} height={12} />}
          </React.Fragment>
        ))}
      </div>
    );
  };

  renderAddShortcutButton = () => {
    if (!this.state.isAddingShortcut) {
      return null;
    }

    return (
      <div
        style={{
          marginBottom: 16,
          display: "flex",
          flexDirection: "column",
          gap: 8
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            gap: 8,
            alignItems: "center"
          }}
        >
          <div className="shortcut-input-wrapper">
            <div className="shortcut-input-overlay">
              {this.renderLiveShortcutBadges(this.state.currentKeyCombo, this.getShortcutPlaceholderText())}
            </div>
            <KuikaInput
              ref={this.shortcutInputRef}
              readOnly
              aria-label="Press key combination for shortcut"
              onClick={this.startCapturingKeyCombo}
              onKeyDown={this.handleKeyDown}
              onKeyUp={this.handleKeyUp}
              onBlur={this.cancelAddingShortcut}
              size="lg"
            />
          </div>
          <KuikaButton
            size="md"
            type="tertiary"
            onClick={this.cancelAddingShortcut}
            style={{ width: 32, height: 32 }}
            aria-label="Cancel adding shortcut"
          >
            <KuikaSvg name="x" type="common" width={20} height={20} />
          </KuikaButton>
        </div>

        <div className="warning-message" role="alert">
          {this.getWarningMessage(this.getShortcutWarningType(this.state.currentKeyCombo))}
        </div>
      </div>
    );
  };

  getUserLocale = (): string => {
    return navigator.language || "en-US";
  };

  isTurkishKeyboard = (): boolean => {
    const locale = this.getUserLocale().toLowerCase();
    return locale.startsWith("tr") || locale === "tr-tr";
  };

  getLocalizedKeyDisplay = (keyCode: string): string | null => {
    if (this.isTurkishKeyboard()) {
      const turkishKeyMap: Record<string, string> = {
        Semicolon: "Ş",
        Quote: "İ",
        Comma: "Ö",
        Period: "Ç",
        Slash: ".",
        BracketLeft: "Ğ",
        BracketRight: "Ü",
        Backslash: ","
      };

      return turkishKeyMap[keyCode] || null;
    }

    return null;
  };

  getSingleKeyDisplay = (key: string, code: string): string => {
    if (key.length === 1) {
      return key.toUpperCase();
    }

    const specialKeyMap: Record<string, string> = {
      ArrowLeft: "Left",
      ArrowRight: "Right",
      ArrowUp: "Up",
      ArrowDown: "Down",
      Escape: "Esc",
      " ": "Space",
      Backspace: "Backspace",
      Delete: "Delete",
      Home: "Home",
      End: "End",
      PageUp: "Page Up",
      PageDown: "Page Down",
      Enter: "Enter",
      Tab: "Tab"
    };

    return specialKeyMap[key] || this.getCodeDisplay(code);
  };

  getCodeDisplay = (code: string): string => {
    if (code.startsWith("Key")) {
      return code.substring(3);
    }
    if (code.startsWith("Digit")) {
      return code.substring(5);
    }
    if (code.startsWith("Arrow")) {
      return code.substring(5);
    }

    const localizedKey = this.getLocalizedKeyDisplay(code);
    if (localizedKey) {
      return localizedKey;
    }

    const displayMap: Record<string, string> = {
      Period: ".",
      Comma: ",",
      Slash: "/",
      Backslash: "\\",
      Semicolon: ";",
      Quote: "'",
      BracketLeft: "[",
      BracketRight: "]",
      Minus: "-",
      Equal: "=",
      Backquote: "`",
      Space: "Space",
      Tab: "Tab",
      Escape: "Esc",
      Enter: "Enter",
      Backspace: "Backspace",
      Delete: "Delete"
    };

    return displayMap[code] || code;
  };

  getKeyDisplay = (key: string): string => {
    if (key === "Meta") {
      return this.getMetaKeyDisplay();
    }
    if (key === "Alt") {
      return this.getAltKeyDisplay();
    }
    if (key === "Ctrl") {
      return "Ctrl";
    }
    if (key === "Shift") {
      return "Shift";
    }

    return this.getCodeDisplay(key);
  };

  renderShortcutBadges = (shortcutKey: string) => {
    const parts = shortcutKey.split("+");

    return (
      <div className="shortcut-badge-container">
        {parts.map((part, index) => (
          <React.Fragment key={`${shortcutKey}-${this.getKeyDisplay(part)}-${index}`}>
            <KuikaBadge content={this.getKeyDisplay(part)} color="blue" />
            {index < parts.length - 1 && <KuikaSvg name="plus" type="common" width={12} height={12} />}
          </React.Fragment>
        ))}
      </div>
    );
  };

  renderShortcutKeysInput = (shortcutKey: string) => {
    const isEditing = this.state.editingShortcutKey === shortcutKey;
    const warningType = this.getShortcutWarningType(shortcutKey);
    const isReserved = warningType !== "none";

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <div
          className={classNames("shortcut-key-display", {
            "shortcut-key-display-editing": isEditing,
            "shortcut-key-display-static": !isEditing
          })}
          onClick={
            isEditing
              ? () => this.editShortcutInputRefs[shortcutKey]?.focus()
              : () => this.startEditingShortcut(shortcutKey)
          }
          role="button"
          tabIndex={0}
          aria-label={`${isEditing ? "Press keys to edit" : "Edit"} shortcut ${shortcutKey
            .split("+")
            .map(this.getKeyDisplay)
            .join(" plus ")}`}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              if (!isEditing) {
                this.startEditingShortcut(shortcutKey);
              }
            }
          }}
        >
          {isEditing ? (
            <>
              <div className="shortcut-input-overlay">
                {this.renderLiveShortcutBadges(this.state.currentKeyCombo, this.getShortcutPlaceholderText())}
              </div>
              <KuikaInput
                ref={(ref) => (this.editShortcutInputRefs[shortcutKey] = ref)}
                className="shortcut-input-hidden"
                readOnly
                autoFocus
                aria-label="Press key combination for shortcut"
                onKeyDown={(e) => this.handleKeyDown(e)}
                onKeyUp={(e) => this.handleKeyUp(e)}
                onBlur={this.cancelEditingShortcut}
              />
            </>
          ) : (
            this.renderShortcutBadges(shortcutKey)
          )}
        </div>

        {isEditing && (
          <div className="warning-message" role="alert">
            {this.getWarningMessage(this.getShortcutWarningType(shortcutKey))}
          </div>
        )}

        {isReserved && !isEditing && (
          <div className="warning-message" role="alert">
            {this.getWarningMessage(this.getShortcutWarningType(shortcutKey))}
          </div>
        )}
      </div>
    );
  };

  renderShortcutsList = () => {
    const allDi: IDisplayItem[] = this.getAllDi();

    return this.state.activeShortcuts.map((shortcut, index) => {
      const menuOptions = [
        {
          key: "edit",
          label: "Edit",
          icon: <KuikaSvg name="edit" type="common" width={16} height={16} />,
          onClick: () => this.startEditingShortcut(shortcut.key)
        },
        {
          key: "delete",
          label: "Delete",
          icon: <KuikaSvg name="trash" color="var(--red-5)" type="common" width={16} height={16} />,
          onClick: () => this.deleteShortcut(shortcut.key),
          danger: true
        }
      ];

      return (
        <div
          key={shortcut.key}
          className="shortcut-card"
          role="region"
          aria-label={`Shortcut ${shortcut.key.split("+").map(this.getKeyDisplay).join(" plus ")}`}
        >
          <div className="shortcut-card-header">
            <KuikaSmallMediumText className="panel-subtitle">Shortcut Keys</KuikaSmallMediumText>
            <KuikaDropdownMenu options={menuOptions} trigger="click">
              {(open) => (
                <KuikaButton isIcon type="tertiary" color="neutral" active={open}>
                  <KuikaSvg width={16} height={16} name="dots" />
                </KuikaButton>
              )}
            </KuikaDropdownMenu>
          </div>

          <div style={{ marginBottom: 16 }}>{this.renderShortcutKeysInput(shortcut.key)}</div>

          <div style={{ marginBottom: 8 }}>
            <KuikaSmallMediumText className="panel-subtitle">Associated Button</KuikaSmallMediumText>
          </div>

          <KuikaSelectbox
            allowClear
            searchable
            style={{ width: "100%" }}
            placeholder="Select button..."
            data-testid="screenShortcutPropertySelectButtonDropdown"
            value={this.getSelectedComponentForShortcut(shortcut.key)}
            onChange={(val: any) => {
              this.handleComponentSelect(shortcut.key, val);
            }}
            className="kuika_add_actions_inputs_size"
            options={allDi.map((di, i) => {
              return { label: di.friendlyName, value: i };
            })}
            aria-label="Select button to associate with this shortcut"
          />
        </div>
      );
    });
  };

  renderShortcutsContent = (): JSX.Element => {
    if (!this.state.isShortcutsPanelActive) {
      return <></>;
    }

    const hasShortcuts = this.state.activeShortcuts.length > 0;
    const isAdding = this.state.isAddingShortcut;

    return (
      <>
        <div style={{ marginBottom: 16 }}>
          <div className="shortcut-section-header">
            <KuikaSmallMediumText>Active Shortcuts ({this.state.activeShortcuts.length})</KuikaSmallMediumText>

            {!isAdding && (
              <KuikaButton
                data-testid="screenShortcutAddButton"
                type="primary"
                size="sm"
                onClick={this.startAddingShortcut}
                aria-label="Add new shortcut"
              >
                <KuikaSvg name="plus" type="common" width={14} height={14} />
                <span>Add</span>
              </KuikaButton>
            )}
          </div>
        </div>

        {isAdding && this.renderAddShortcutButton()}

        {!hasShortcuts && !isAdding && (
          <>
            <div style={{ marginBottom: 20, textAlign: "center" }}>
              <KuikaSmallMediumText>
                Shortcuts allow you to trigger buttons with keyboard combinations.
              </KuikaSmallMediumText>
            </div>
            <KuikaEmptyState
              variant="embedded"
              svgName="keyboard"
              name="Shortcuts"
              customName="No Shortcuts added yet."
            />
          </>
        )}

        {hasShortcuts && <div role="list">{this.renderShortcutsList()}</div>}
      </>
    );
  };

  render() {
    return (
      <div className="shortcuts-panel">
        <KuikaCollapsePanel
          destroyInactivePanel
          activeKey={this.state.isShortcutsPanelActive ? "shortcuts-panel" : ""}
          onChange={() => {
            this.setState({
              isShortcutsPanelActive: !this.state.isShortcutsPanelActive
            });
          }}
          iconPlacement="right"
          className="kuika-right-panel__collapse kuika-right-panel__collapse-lighter kuika-right-panel__collapse--boxed kuika-right-panel__collapse--shortcuts"
          expandIcon={this.renderExpandIcon}
          items={[
            {
              key: "shortcuts-panel",
              label: (
                <>
                  <PropertyRowIcon kind="shortcuts" />
                  <KuikaTextMedium data-onboarding-id="shortcuts-panel-header-title">Shortcuts</KuikaTextMedium>
                </>
              ),
              children: (
                <div className="kuika-right-panel__collapse-body">
                  <div className="kuika-right-panel__col kuika_property-grid-column kuika_select_property shortcuts-panel__scroller">
                    {this.renderShortcutsContent()}
                  </div>
                </div>
              )
            }
          ]}
        />
      </div>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    if (
      changeReason === ESdChangeReason.UpdateShortcuts ||
      changeReason === ESdChangeReason.DiActionUpdated ||
      changeReason === ESdChangeReason.ChangeSelectedDiByCanvasFrame ||
      changeReason === ESdChangeReason.ChangeSelectedDiByTools ||
      changeReason === ESdChangeReason.DragDropDi ||
      changeReason === ESdChangeReason.DragDropBlock
    ) {
      return true;
    }
    return false;
  };
}

const shortcutsPanel = withSD<IShortcutsPanelProps>(ShortcutsPanel);
export { shortcutsPanel as ShortcutsPanel };
